import {
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material"
import { orange } from "@mui/material/colors"
import { LoadingIcon } from "components/Common/LoadingIcon"
import { useAppDispatch, useAppSelector } from "hooks"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import authService from "services/authService"
import licensePoolService from "services/licensePoolService"
import { setCurrentLicensePool, setLicensePools } from "slices/licensePoolSlice"
import { hiabColors } from "theme"
import type { ILicensePool } from "types/licensePoolTypes"
import type { IOrganization } from "types/organizationTypes"
import { UserRoles } from "types/userRoles"
import { LicensePoolCardMenu } from "./LicensePoolCardMenu"

type Props = {
  organization: IOrganization
}

export function LicensePoolList({ organization }: Props) {
  const [roles, setRoles] = useState<Array<string>>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const licensePools = useAppSelector((state) => state.licensepool.licensePools)

  useEffect(() => {
    authService.getRolesAssignedToUser().then((roles) => {
      setRoles(roles)
    })

    if (licensePools.length === 0) {
      setIsLoading(true)

      licensePoolService
        .getOrganizationLicensePools(organization.id)
        .then((licensePools) => {
          dispatch(setLicensePools(licensePools))
          setIsLoading(false)
        })
        .catch((err) => {
          console.log(err)
        })
    }
  }, [dispatch, licensePools.length, organization.id])

  const handleClickLicensePool = (licensePool: ILicensePool) => {
    dispatch(setCurrentLicensePool(licensePool))
    navigate(licensePool.id)
  }

  const contractEndDateClosing = (licPool: ILicensePool): boolean => {
    const now = new Date()
    const offsetDate = new Date(now.setMonth(now.getMonth() + 2))
    const diff = offsetDate.getTime() - new Date(licPool.contractEndDate).getTime()
    return diff > 0
  }

  const contractEndDateExpired = (licPool: ILicensePool): boolean => {
    const diff = Date.now() - new Date(licPool.contractEndDate).getTime()
    return diff > 0
  }

  return (
    <>
      {isLoading ? (
        <LoadingIcon />
      ) : (
        <Grid container spacing={2}>
          {licensePools.map((licensePool) => (
            <Grid key={licensePool.id} item lg={4} md={6} xs={12}>
              <Card>
                <CardHeader
                  title={licensePool.name}
                  action={
                    roles?.includes(UserRoles.HIAB_ADMIN) && (
                      <LicensePoolCardMenu licensePool={licensePool} />
                    )
                  }
                />
                <CardActionArea onClick={() => handleClickLicensePool(licensePool)}>
                  <CardContent>
                    <Grid container spacing={1}>
                      <Grid item container>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Created:
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body1" color="text.primary">
                            {new Date(licensePool.createdDate).toLocaleDateString("en-US")}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        container
                        borderBottom={contractEndDateExpired(licensePool) ? 1 : 0}
                        borderColor={hiabColors.redPrimary}
                      >
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Contract End Date:
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            variant="body1"
                            color={
                              contractEndDateExpired(licensePool)
                                ? hiabColors.redPrimary
                                : contractEndDateClosing(licensePool)
                                  ? orange[700]
                                  : "text.primary"
                            }
                          >
                            {new Date(licensePool.contractEndDate).toLocaleDateString("en-US")}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item container>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Free Licenses
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body1" color="text.primary">
                            {licensePool.freeLicenses}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item container>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Assigned Licenses
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body1" color="text.primary">
                            {licensePool.assignedLicenses}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item container>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            License Offline Access Duration
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body1" color="text.primary">
                            {`${licensePool.licenseOfflineDuration} d`}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item container>
                        <Grid item xs={12} marginBottom={1}>
                          <Divider />
                          <Typography variant="body2" color="text.secondary" marginTop={1}>
                            Training Modules
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {licensePool.trainingModules.map((module) => (
                              <Chip key={module.id} label={`${module.simulatorApplicationName}: ${module.name}`} variant="outlined" />
                            ))}
                          </Stack>
                        </Grid>
                      </Grid>
                    </Grid>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </>
  )
}
